/**
 * AnalyticsLandingPage — lists the user's analytics dashboards and
 * shared dashboards with pagination, sorted by last modified descending.
 *
 * Requirements: 1.3, 1.4, 1.6, 3.6
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as analyticsApi from '../api/analytics-api';
import type { AnalyticsDashboard, SharedDashboard } from '../api/analytics-api';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { TemplateSelector } from '../components/analytics/TemplateSelector';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PAGE_SIZE = 20;

/**
 * Truncates a title to a maximum of 100 characters, appending "..." if truncated.
 */
function truncateTitle(title: string): string {
  if (title.length <= 100) return title;
  return title.slice(0, 100) + '...';
}

/**
 * Formats an ISO timestamp as a relative date string (e.g., "2 hours ago").
 */
function formatRelativeDate(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (weeks < 5) return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

/**
 * Counts the distinct boards referenced across all widgets in a dashboard layout.
 * Since we only have layout info (widgetId, col, row, width, height) on the
 * listing response, we display the widget count as the board source count proxy.
 */
function getBoardSourceCount(dashboard: AnalyticsDashboard): number {
  return dashboard.widgetLayout?.length ?? 0;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AnalyticsLandingPage(): React.ReactElement {
  const navigate = useNavigate();

  // My Dashboards state
  const [dashboards, setDashboards] = useState<AnalyticsDashboard[]>([]);
  const [dashboardNextCursor, setDashboardNextCursor] = useState<string | null>(null);
  const [dashboardCursorStack, setDashboardCursorStack] = useState<string[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  // Shared Dashboards state
  const [sharedDashboards, setSharedDashboards] = useState<SharedDashboard[]>([]);
  const [sharedNextCursor, setSharedNextCursor] = useState<string | null>(null);
  const [sharedCursorStack, setSharedCursorStack] = useState<string[]>([]);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [sharedError, setSharedError] = useState<string | null>(null);

  // Creation state
  const [creating, setCreating] = useState(false);

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const fetchMyDashboards = useCallback(async (cursor?: string) => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const result = await analyticsApi.listMyDashboards(cursor, PAGE_SIZE);
      setDashboards(result.dashboards);
      setDashboardNextCursor(result.nextCursor);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load dashboards';
      setDashboardError(message);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  const fetchSharedDashboards = useCallback(async (cursor?: string) => {
    setSharedLoading(true);
    setSharedError(null);
    try {
      const result = await analyticsApi.listSharedDashboards(cursor, PAGE_SIZE);
      setSharedDashboards(result.dashboards);
      setSharedNextCursor(result.nextCursor);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load shared dashboards';
      setSharedError(message);
    } finally {
      setSharedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyDashboards();
    fetchSharedDashboards();
  }, [fetchMyDashboards, fetchSharedDashboards]);

  // ---------------------------------------------------------------------------
  // Pagination handlers
  // ---------------------------------------------------------------------------

  const handleDashboardNext = useCallback(() => {
    if (!dashboardNextCursor) return;
    // Push current cursor onto stack so we can go back
    setDashboardCursorStack((prev) => [...prev, dashboardNextCursor]);
    fetchMyDashboards(dashboardNextCursor);
  }, [dashboardNextCursor, fetchMyDashboards]);

  const handleDashboardPrev = useCallback(() => {
    setDashboardCursorStack((prev) => {
      const stack = [...prev];
      stack.pop(); // Remove current page's cursor
      const prevCursor = stack[stack.length - 1];
      // Fetch with previous cursor (undefined = first page)
      fetchMyDashboards(prevCursor || undefined);
      return stack;
    });
  }, [fetchMyDashboards]);

  const handleSharedNext = useCallback(() => {
    if (!sharedNextCursor) return;
    setSharedCursorStack((prev) => [...prev, sharedNextCursor]);
    fetchSharedDashboards(sharedNextCursor);
  }, [sharedNextCursor, fetchSharedDashboards]);

  const handleSharedPrev = useCallback(() => {
    setSharedCursorStack((prev) => {
      const stack = [...prev];
      stack.pop();
      const prevCursor = stack[stack.length - 1];
      fetchSharedDashboards(prevCursor || undefined);
      return stack;
    });
  }, [fetchSharedDashboards]);

  // ---------------------------------------------------------------------------
  // Dashboard creation
  // ---------------------------------------------------------------------------

  const handleCreateDashboard = useCallback(async () => {
    setCreating(true);
    try {
      const newDashboard = await analyticsApi.createDashboard();
      navigate(`/analytics/dashboards/${newDashboard.dashboardId}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create dashboard';
      setDashboardError(message);
    } finally {
      setCreating(false);
    }
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <main className="min-h-screen bg-background p-6" role="main">
      <div className="mx-auto max-w-7xl">
        <header className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-foreground">Analytics</h1>
          <Button
            onClick={handleCreateDashboard}
            disabled={creating}
            aria-label="Create new analytics dashboard"
          >
            {creating ? 'Creating...' : 'New Dashboard'}
          </Button>
        </header>

        {/* My Dashboards Section */}
        <section aria-labelledby="my-dashboards-heading" className="mb-10">
          <h2 id="my-dashboards-heading" className="text-lg font-semibold text-foreground mb-4">
            My Dashboards
          </h2>

          {dashboardLoading && dashboards.length === 0 && (
            <div className="flex items-center py-8" role="status" aria-label="Loading dashboards">
              <div
                className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
                aria-hidden="true"
              />
              <span className="ml-3 text-muted-foreground">Loading dashboards…</span>
            </div>
          )}

          {dashboardError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{dashboardError}</AlertDescription>
            </Alert>
          )}

          {!dashboardLoading && !dashboardError && dashboards.length === 0 && (
            <div className="rounded-lg border border-border bg-muted/50 p-8 text-center">
              <p className="text-muted-foreground mb-4">
                You don't have any analytics dashboards yet. Create one to start visualizing your board data.
              </p>
              <Button onClick={handleCreateDashboard} disabled={creating}>
                Create Dashboard
              </Button>
            </div>
          )}

          {dashboards.length > 0 && (
            <>
              <ul className="space-y-2" role="list" aria-label="My dashboards list">
                {dashboards.map((dashboard) => (
                  <li key={dashboard.dashboardId}>
                    <button
                      type="button"
                      className="w-full text-left rounded-lg border border-border p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={() => navigate(`/analytics/dashboards/${dashboard.dashboardId}`)}
                      aria-label={`Open dashboard: ${dashboard.title}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">
                          {truncateTitle(dashboard.title)}
                        </span>
                        <span className="text-sm text-muted-foreground ml-4 shrink-0">
                          {getBoardSourceCount(dashboard)} source{getBoardSourceCount(dashboard) !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <time
                        dateTime={dashboard.updatedAt}
                        className="text-sm text-muted-foreground mt-1 block"
                      >
                        Modified {formatRelativeDate(dashboard.updatedAt)}
                      </time>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Pagination controls */}
              <nav aria-label="My dashboards pagination" className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDashboardPrev}
                  disabled={dashboardCursorStack.length === 0}
                  aria-label="Previous page of dashboards"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDashboardNext}
                  disabled={!dashboardNextCursor}
                  aria-label="Next page of dashboards"
                >
                  Next
                </Button>
              </nav>
            </>
          )}
        </section>

        {/* Shared With Me Section */}
        <section aria-labelledby="shared-dashboards-heading">
          <h2 id="shared-dashboards-heading" className="text-lg font-semibold text-foreground mb-4">
            Shared with me
          </h2>

          {sharedLoading && sharedDashboards.length === 0 && (
            <div className="flex items-center py-8" role="status" aria-label="Loading shared dashboards">
              <div
                className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
                aria-hidden="true"
              />
              <span className="ml-3 text-muted-foreground">Loading shared dashboards…</span>
            </div>
          )}

          {sharedError && (
            <Alert variant="destructive" className="mb-4">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{sharedError}</AlertDescription>
            </Alert>
          )}

          {!sharedLoading && !sharedError && sharedDashboards.length === 0 && (
            <p className="text-muted-foreground py-4">
              No dashboards have been shared with you yet.
            </p>
          )}

          {sharedDashboards.length > 0 && (
            <>
              <ul className="space-y-2" role="list" aria-label="Shared dashboards list">
                {sharedDashboards.map((dashboard) => (
                  <li key={dashboard.dashboardId}>
                    <button
                      type="button"
                      className="w-full text-left rounded-lg border border-border p-4 hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={() => navigate(`/analytics/dashboards/${dashboard.dashboardId}`)}
                      aria-label={`Open shared dashboard: ${dashboard.title}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">
                          {truncateTitle(dashboard.title)}
                        </span>
                        <span className="text-sm text-muted-foreground ml-4 shrink-0">
                          by {dashboard.ownerDisplayName}
                        </span>
                      </div>
                      <time
                        dateTime={dashboard.updatedAt}
                        className="text-sm text-muted-foreground mt-1 block"
                      >
                        Modified {formatRelativeDate(dashboard.updatedAt)}
                      </time>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Pagination controls */}
              <nav aria-label="Shared dashboards pagination" className="flex items-center justify-between mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSharedPrev}
                  disabled={sharedCursorStack.length === 0}
                  aria-label="Previous page of shared dashboards"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSharedNext}
                  disabled={!sharedNextCursor}
                  aria-label="Next page of shared dashboards"
                >
                  Next
                </Button>
              </nav>
            </>
          )}
        </section>

        {/* Templates section */}
        <TemplateSelector />
      </div>
    </main>
  );
}
